import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ThemeProvider } from 'styled-components';
import { Icon } from 'components/v3';
import PageHeader from 'components/v3/PageHeader';
import pitpayLogo from './assets/pitpay-logo.svg';
import {
  Card,
  CardHeader,
  ExpandButton,
  PillToggle,
  StatTile,
  Modal,
} from './components';
import {
  getIconUrl,
  getBarColor,
  RADAR_FRAMES,
  NOW_INDEX,
  LAST_FRAME,
  framePos,
} from './data';
import { MODAL_TITLES, MODAL_ICON_NAMES, buildModalBody } from './modals';
import RadarMap from './RadarMap';
import { WCRoot } from './styles';
import { useGetWeatherCenter } from './useGetWeatherCenter';
import { buildViewModel } from './view-model';
// Vortex "Presented by" logo — hidden until that deal starts.
// import vortexLogo from './assets/vortex-logo.svg';

const RADAR_TABS = [
  { id: 'radar', label: 'Radar' },
  { id: 'lightning', label: 'Lightning' },
  { id: 'storms', label: 'Storm Cells' },
  { id: 'alerts', label: 'Alerts' },
];

const LAYER_CHIPS = [
  { label: 'Precipitation', on: true },
  { label: 'Lightning', on: true },
  { label: 'Alerts', on: false },
  { label: 'Storm Cells', on: true },
  { label: 'Wind', on: false },
];

const LIGHTNING_STATUS = {
  danger: { cls: 'danger', text: 'Danger', sub: 'Lightning within 10 miles' },
  caution: {
    cls: 'caution',
    text: 'Caution',
    sub: 'Lightning within 25 miles',
  },
  clear: { cls: 'clear', text: 'All Clear', sub: 'No nearby strikes' },
};
const statusInfo = (status) =>
  LIGHTNING_STATUS[status] || {
    cls: '',
    text: '—',
    sub: 'Lightning data unavailable',
  };

// Keyboard + role props for clickable divs/spans, so they behave like buttons
// for assistive tech without disturbing the design markup.
const buttonize = (fn) => ({
  role: 'button',
  tabIndex: 0,
  onClick: fn,
  onKeyDown: (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fn(e);
    }
  },
});

// On phones the full 13-frame scrubber is unusably cramped, so we surface a
// reduced set of key frames as bigger, labeled, evenly-spaced tap stops. Mobile
// playback + stepping ride these indices so the active dot always lands on one.
const MOBILE_KEY_MINS = [-60, -30, 0, 30, 60, 120];
const MOBILE_LABELS = {
  '-60': '-1h',
  '-30': '-30',
  0: 'NOW',
  30: '+30',
  60: '+1h',
  120: '+2h',
};
const MOBILE_INDICES = MOBILE_KEY_MINS.map((m) =>
  RADAR_FRAMES.findIndex((f) => f.mins === m)
).filter((i) => i >= 0);

function useIsMobile(bp = 560) {
  const query = `(max-width: ${bp}px)`;
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [query]);
  return isMobile;
}

const WeatherCenter = () => {
  const { data, loading } = useGetWeatherCenter();
  const wc = data && data.getWeatherCenter;
  const vm = buildViewModel(wc);

  const [alertExpanded, setAlertExpanded] = useState(false);
  const [activeRadarTab, setActiveRadarTab] = useState('radar');
  const [layers, setLayers] = useState(() =>
    LAYER_CHIPS.reduce((acc, c) => ({ ...acc, [c.label]: c.on }), {})
  );
  const [forecastRange, setForecastRange] = useState(10);
  const [selectedDay, setSelectedDay] = useState(0);
  const [radarFrame, setRadarFrame] = useState(NOW_INDEX);
  const [radarPlaying, setRadarPlaying] = useState(true);
  const [modal, setModal] = useState(null); // { type } | { type:'hourly', day }
  const [phrasesOpen, setPhrasesOpen] = useState(false);
  const isMobile = useIsMobile();
  // Leaflet map instances (panel + fullscreen modal) for wiring zoom controls.
  const panelMapRef = useRef(null);
  const modalMapRef = useRef(null);

  // Entering mobile: snap to a key frame so the active dot lands on a visible tick.
  useEffect(() => {
    if (isMobile && !MOBILE_INDICES.includes(radarFrame))
      setRadarFrame(NOW_INDEX);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Radar playback loop — advances the frame offset that drives the real tiles.
  // On mobile it hops through the reduced key-frame set instead of all frames.
  useEffect(() => {
    if (!radarPlaying) return undefined;
    let delay = 650;
    if (radarFrame === NOW_INDEX) delay = 1300;
    else if (radarFrame === LAST_FRAME) delay = 1800;
    const t = setTimeout(() => {
      setRadarFrame((f) => {
        if (isMobile) {
          const pos = MOBILE_INDICES.indexOf(f);
          return MOBILE_INDICES[
            pos === -1 ? 0 : (pos + 1) % MOBILE_INDICES.length
          ];
        }
        return f === LAST_FRAME ? 0 : f + 1;
      });
    }, delay);
    return () => clearTimeout(t);
  }, [radarFrame, radarPlaying, isMobile]);

  const jumpRadarFrame = useCallback((idx) => {
    setRadarPlaying(false);
    setRadarFrame(idx);
  }, []);

  const toggleLayer = (label) =>
    setLayers((l) => ({ ...l, [label]: !l[label] }));
  const viewOnRadar = (tab) => setActiveRadarTab(tab);
  const openModal = (type) => setModal({ type });
  const closeModal = () => setModal(null);
  const selectDay = (i, day) => {
    setSelectedDay(i);
    setModal({ type: 'hourly', day });
  };

  const frame = RADAR_FRAMES[radarFrame];
  const radarLat = vm && vm.location ? vm.location.lat : null;
  const radarLng = vm && vm.location ? vm.location.lng : null;
  const isNow = radarFrame === NOW_INDEX;
  const isFuture = !!frame.future;
  const nowPos = `${framePos(NOW_INDEX)}%`;
  const progress = `${framePos(radarFrame)}%`;
  const frameLabel = isNow ? 'NOW' : isFuture ? `+${frame.mins}m` : frame.label;
  // Real clock time for a frame = now + its minute offset, shown in the venue's
  // timezone (falls back to the browser's if the tz is unknown).
  const venueTz = vm && vm.location ? vm.location.timezone : null;
  const clockAt = (mins) => {
    const d = new Date(Date.now() + mins * 60000);
    try {
      return d.toLocaleTimeString('en-US', {
        timeZone: venueTz || undefined,
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch (e) {
      return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      });
    }
  };
  let tzAbbr = '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: venueTz || undefined,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    const p = parts.find((x) => x.type === 'timeZoneName');
    tzAbbr = p ? p.value : '';
  } catch (e) {
    tzAbbr = '';
  }
  const timestamp = `${clockAt(frame.mins)}${
    isFuture ? ' · forecast' : tzAbbr ? ` ${tzAbbr}` : ''
  }`;
  const liveDotColor = isNow
    ? '#22C55E'
    : isFuture
    ? 'var(--orange)'
    : 'var(--text-tertiary)';

  // ---- Radar map (real Leaflet; shared by panel + fullscreen modal) ----
  const renderRadarMap = (_scale, mapStyle, mapRef) => (
    <div className="radar-map-wrap" style={mapStyle}>
      <RadarMap
        lat={radarLat}
        lng={radarLng}
        name={venueName}
        mins={frame.mins}
        layers={layers}
        stormCells={vm.stormCells}
        interactive
        onMap={(m) => {
          if (mapRef) mapRef.current = m;
        }}
      />
      <div className="radar-timestamp">
        <span
          className="live-dot-sm"
          style={{
            background: liveDotColor,
            animationPlayState: isNow ? 'running' : 'paused',
          }}
        />{' '}
        <span>{timestamp}</span>
      </div>
      <div className={`radar-forecast-badge${isFuture ? ' active' : ''}`}>
        <Icon name="clock" size={11} strokeWidth={2.5} />
        <span>
          <span className="fc-lead">+{frame.mins} min</span> &middot; Forecast
        </span>
      </div>
      <div className="radar-zoom">
        <button
          type="button"
          className="radar-zoom-btn"
          onClick={() => mapRef && mapRef.current && mapRef.current.zoomIn()}
        >
          +
        </button>
        <button
          type="button"
          className="radar-zoom-btn"
          onClick={() => mapRef && mapRef.current && mapRef.current.zoomOut()}
        >
          &minus;
        </button>
      </div>
      <div className="radar-scale">
        <div className="radar-scale-bar" />
        <span className="radar-scale-label">10 mi</span>
      </div>
      <div className="radar-legend">
        <div className="radar-legend-title">Precipitation</div>
        <div className="radar-legend-items">
          <div className="radar-legend-item">
            <div
              className="radar-legend-swatch"
              style={{ background: 'var(--rain-light)' }}
            />{' '}
            Light (&lt;0.1 in/hr)
          </div>
          <div className="radar-legend-item">
            <div
              className="radar-legend-swatch"
              style={{ background: 'var(--rain-moderate)' }}
            />{' '}
            Moderate (0.1-0.25)
          </div>
          <div className="radar-legend-item">
            <div
              className="radar-legend-swatch"
              style={{ background: 'var(--rain-heavy)' }}
            />{' '}
            Heavy (0.25-0.5)
          </div>
          <div className="radar-legend-item">
            <div
              className="radar-legend-swatch"
              style={{ background: 'var(--rain-extreme)' }}
            />{' '}
            Extreme (&gt;0.5)
          </div>
        </div>
      </div>
    </div>
  );

  const renderRadarTimeline = () => (
    <div className="radar-timeline">
      <button
        type="button"
        className="radar-play-btn"
        onClick={() => setRadarPlaying((p) => !p)}
        aria-label="Play/pause radar"
        title="Play/pause loop"
      >
        <Icon
          name={radarPlaying ? 'pause' : 'play'}
          size={14}
          color="var(--orange)"
        />
      </button>
      <div className="radar-timeline-track">
        <div
          className="radar-timeline-line"
          style={{ '--now-pos': nowPos, '--progress': progress }}
        />
        <div className="radar-now-divider" style={{ '--now-pos': nowPos }} />
        <div className={`radar-timeline-ticks${isMobile ? ' mobile' : ''}`}>
          {(isMobile ? MOBILE_INDICES : RADAR_FRAMES.map((_, i) => i)).map(
            (i) => {
              const f = RADAR_FRAMES[i];
              const label = isMobile
                ? MOBILE_LABELS[f.mins] || f.label
                : f.label;
              const cls = [
                'radar-tick-wrap',
                i === radarFrame ? 'active' : '',
                i === NOW_INDEX ? 'now' : '',
                f.future ? 'future' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <div
                  key={i}
                  className={cls}
                  style={isMobile ? undefined : { '--pos': `${framePos(i)}%` }}
                  {...buttonize(() => jumpRadarFrame(i))}
                >
                  <div className="radar-tick" />
                  <div className="radar-tick-label">{label}</div>
                </div>
              );
            }
          )}
        </div>
      </div>
      <div
        className={`radar-timeline-time${isNow ? ' is-now' : ''}${
          isFuture ? ' is-future' : ''
        }`}
      >
        {frameLabel}
      </div>
    </div>
  );

  const layerChips = LAYER_CHIPS.map((c) => (
    <PillToggle
      key={c.label}
      variant="layer"
      active={layers[c.label]}
      onClick={() => toggleLayer(c.label)}
    >
      {c.label}
    </PillToggle>
  ));

  // ---- Modal header data ----
  const isRadarModal = modal && modal.type === 'radar';
  let modalTitle = '';
  let modalIconName = null;
  if (modal) {
    if (modal.type === 'hourly') {
      const d = modal.day;
      modalTitle = `${d.isToday ? 'Today' : d.day} ${d.date} — Hour by Hour`;
      modalIconName = 'calendar';
    } else {
      modalTitle = MODAL_TITLES[modal.type] || 'Detail View';
      modalIconName = MODAL_ICON_NAMES[modal.type] || null;
    }
  }

  const renderHourlyModalBody = (d) => {
    if (!d.hourly || !d.hourly.length) {
      return (
        <div
          style={{
            padding: '24px 4px',
            color: 'var(--text-muted)',
            fontSize: 13,
            textAlign: 'center',
          }}
        >
          {d.summary ? (
            <div
              style={{
                marginBottom: 12,
                color: 'var(--text-secondary)',
                lineHeight: 1.6,
              }}
            >
              {d.summary}
            </div>
          ) : null}
          Hour-by-hour detail isn’t available for this day yet.
        </div>
      );
    }
    return (
      <>
        {d.summary ? (
          <div
            style={{
              padding: '0 0 16px',
              fontSize: 13,
              lineHeight: 1.6,
              color: 'var(--text-secondary)',
              borderBottom: '1px solid var(--border)',
              marginBottom: 16,
            }}
          >
            {d.summary}
          </div>
        ) : null}
        <div className="hourly-table">
          <table>
            <thead>
              <tr>
                <th style={{ fontSize: 12 }}>Time</th>
                <th />
                <th style={{ fontSize: 12 }}>Precip %</th>
                <th style={{ fontSize: 12 }}>Rainfall</th>
                <th style={{ fontSize: 12 }}>Temp</th>
                <th style={{ fontSize: 12 }}>Wind</th>
                <th style={{ fontSize: 12 }}>Humidity</th>
              </tr>
            </thead>
            <tbody>
              {d.hourly.map((h, i) => (
                <tr key={i}>
                  <td
                    style={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: 'var(--text-primary)',
                    }}
                  >
                    {h.time}
                  </td>
                  <td>
                    {h.icon ? (
                      <span className="hour-icon">
                        <img src={getIconUrl(h.icon)} alt="" />
                      </span>
                    ) : null}
                  </td>
                  <td style={{ fontSize: 15 }}>
                    {h.precipPct == null ? '—' : `${h.precipPct}%`}
                  </td>
                  <td style={{ fontSize: 15 }}>
                    {h.rainIn == null ? '—' : `${h.rainIn.toFixed(2)} in`}
                  </td>
                  <td style={{ fontSize: 15, color: 'var(--text-muted)' }}>
                    {h.tempF == null ? '—' : `${h.tempF}°`}
                  </td>
                  <td style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                    {h.windMph == null ? '—' : `${h.windMph} mph`}
                  </td>
                  <td style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                    {h.humidity == null ? '—' : `${h.humidity}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  // ---- Venue label (from resolved location) ----
  const venueLabel = vm && vm.venue ? vm.venue.label : null;
  const venueName = (vm && vm.venue && vm.venue.name) || 'Venue';
  const venueSubtitle = venueLabel ? (
    <>
      <Icon name="map-pin" size={15} color="var(--orange)" strokeWidth={2.2} />
      {venueLabel}
    </>
  ) : null;

  const renderEmptyState = () => (
    <div className="dashboard">
      <PageHeader
        icon="cloud-lightning"
        tileSize={64}
        logo={pitpayLogo}
        title="Weather Center"
      />
      <Card>
        <div
          style={{
            padding: '48px 16px',
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}
        >
          {loading ? (
            'Loading weather…'
          ) : (
            <>
              <div
                style={{
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 6,
                }}
              >
                Weather unavailable
              </div>
              No upcoming event or venue address found for this account.
            </>
          )}
        </div>
      </Card>
    </div>
  );

  const forecast = vm ? vm.forecast : [];
  const visibleDays = forecast.slice(0, forecastRange);
  const alert = vm && vm.alerts.length ? vm.alerts[0] : null;
  const lightning = vm ? vm.lightning : null;
  const li = statusInfo(lightning && lightning.status);

  return (
    <ThemeProvider theme={{ name: 'dark' }}>
      <WCRoot>
        {!vm ? (
          renderEmptyState()
        ) : (
          <div className="dashboard">
            {/* HEADER */}
            <PageHeader
              icon="cloud-lightning"
              tileSize={64}
              logo={pitpayLogo}
              title="Weather Center"
              subtitle={venueSubtitle}
            />

            {/* ALERT BANNER (only when there's an active alert) */}
            {alert && (
              <div
                className={`alert-banner${alertExpanded ? ' expanded' : ''}`}
                {...buttonize(() => setAlertExpanded((e) => !e))}
              >
                <div className="alert-banner-header">
                  <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                    <Icon
                      name="triangle-alert"
                      size={26}
                      color="white"
                      strokeWidth={2}
                    />
                  </span>
                  <span className="alert-type">{alert.type}</span>
                  <span className="alert-summary">
                    {alert.expires && (
                      <span className="alert-until-pill">
                        <Icon
                          name="clock"
                          size={12}
                          color="white"
                          strokeWidth={2.5}
                        />
                        <span className="until-label">Until</span>
                        <span>{alert.expires}</span>
                      </span>
                    )}
                    {alert.county && (
                      <span className="alert-county">{alert.county}</span>
                    )}
                  </span>
                  <span
                    className="alert-chevron"
                    style={{ display: 'inline-flex' }}
                  >
                    <Icon
                      name="chevron-down"
                      size={18}
                      color="rgba(255,255,255,0.7)"
                      strokeWidth={2}
                    />
                  </span>
                </div>
                <div className="alert-detail">
                  <div className="alert-detail-grid">
                    {alert.body && <p>{alert.body}</p>}
                    <div className="alert-timeline">
                      <div className="atl-item is-done">
                        <span className="atl-node">
                          <Icon name="check" size={16} strokeWidth={2.6} />
                        </span>
                        <span className="atl-text">
                          <span className="atl-label">Issued</span>
                          <span className="atl-time">
                            {alert.issued || '—'}
                          </span>
                        </span>
                      </div>
                      <div className="atl-conn" />
                      <div className="atl-item is-active">
                        <span className="atl-node">
                          <Icon name="play" size={16} />
                        </span>
                        <span className="atl-text">
                          <span className="atl-label">Effective</span>
                          <span className="atl-time">
                            {alert.effective || '—'}
                          </span>
                        </span>
                      </div>
                      <div className="atl-conn" />
                      <div className="atl-item is-end">
                        <span className="atl-node">
                          <Icon name="clock" size={16} strokeWidth={2.4} />
                        </span>
                        <span className="atl-text">
                          <span className="atl-label">Expires</span>
                          <span className="atl-time">
                            {alert.expires || '—'}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div
                      className="radar-hint on-alert"
                      {...buttonize((e) => {
                        e.stopPropagation();
                        viewOnRadar('alerts');
                      })}
                    >
                      <span className="rh-icon">
                        <Icon
                          name="triangle-alert"
                          size={15}
                          color="#fff"
                          strokeWidth={2}
                        />
                      </span>
                      <span className="radar-hint-text">
                        See the affected area on the radar map.
                      </span>
                      <span className="radar-hint-cta">View radar &rarr;</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ROW 2: CONDITIONS + RADAR HUB */}
            <div className="row-top">
              {/* Current Conditions */}
              <Card>
                <CardHeader
                  icon="thermometer"
                  label="Current Conditions"
                  forceLeft
                  tip="Real-time temperature, wind, humidity, pressure, and sky conditions from the nearest Xweather station."
                  actions={
                    <ExpandButton onClick={() => openModal('conditions')} />
                  }
                />
                <div className="temp-row">
                  <div className="weather-icon-wrap">
                    {vm.conditions && vm.conditions.icon ? (
                      <img
                        src={getIconUrl(vm.conditions.icon)}
                        alt={vm.conditions.description}
                      />
                    ) : null}
                  </div>
                  <div>
                    <div className="temp-main">
                      {vm.conditions ? vm.conditions.temp : '—'}
                    </div>
                    <div className="temp-description">
                      {vm.conditions ? vm.conditions.description : '—'}
                    </div>
                    {vm.conditions && vm.conditions.feelsLike && (
                      <div className="temp-feels">
                        {vm.conditions.feelsLike}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className="phrases-trigger"
                  {...buttonize(() => setPhrasesOpen(true))}
                >
                  <Icon name="info" size={28} color="var(--orange)" />
                  <span className="phrases-trigger-text">
                    Today's Forecast Summary
                  </span>
                  <span className="phrases-chevron">&rsaquo;</span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 14,
                  }}
                >
                  <span style={{ fontSize: 18, fontWeight: 600 }}>
                    <span style={{ color: 'var(--temp-warm)' }}>
                      {vm.conditions ? vm.conditions.high : '—'}
                    </span>
                    <span
                      style={{
                        color: 'var(--text-muted)',
                        fontWeight: 400,
                        fontSize: 14,
                      }}
                    >
                      {' '}
                      /{' '}
                    </span>
                    <span style={{ color: 'var(--temp-cool)' }}>
                      {vm.conditions ? vm.conditions.low : '—'}
                    </span>
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    High / Low
                  </span>
                </div>
                <div className="conditions-grid">
                  {(vm.conditions ? vm.conditions.items : []).map(
                    ([label, value, color]) => (
                      <div className="condition-item" key={label}>
                        <span className="condition-label">{label}</span>
                        <span
                          className="condition-value"
                          style={color ? { color } : undefined}
                        >
                          {value}
                        </span>
                      </div>
                    )
                  )}
                </div>
                <div className="sun-row">
                  <div className="sun-item">
                    <span style={{ display: 'inline-flex' }}>
                      <Icon
                        name="sun"
                        size={18}
                        color="var(--orange)"
                        strokeWidth={1.5}
                      />
                    </span>
                    <div>
                      <div className="sun-label">Sunrise</div>
                      <div className="sun-time">
                        {vm.conditions ? vm.conditions.sunrise : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="sun-item">
                    <span style={{ display: 'inline-flex' }}>
                      <Icon
                        name="sunset"
                        size={18}
                        color="var(--orange)"
                        strokeWidth={1.5}
                      />
                    </span>
                    <div>
                      <div className="sun-label">Sunset</div>
                      <div className="sun-time">
                        {vm.conditions ? vm.conditions.sunset : '—'}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Radar Hub */}
              <Card className="radar-hub">
                <CardHeader
                  icon="radar"
                  label="Live Monitoring"
                  tip="Interactive radar map with real-time precipitation, lightning strikes, storm cell tracking, active alerts, and wind overlays. Toggle layers below the map."
                  actions={
                    <ExpandButton onClick={() => openModal(activeRadarTab)} />
                  }
                />

                <div className="radar-tabs">
                  {RADAR_TABS.map((t) => (
                    <PillToggle
                      key={t.id}
                      variant="tab"
                      active={activeRadarTab === t.id}
                      onClick={() => setActiveRadarTab(t.id)}
                    >
                      {t.label}
                    </PillToggle>
                  ))}
                </div>

                {/* Radar panel (real Leaflet map) */}
                <div
                  className={`radar-tab-panel${
                    activeRadarTab === 'radar' ? ' active' : ''
                  }`}
                >
                  {renderRadarMap(1.0, undefined, panelMapRef)}
                  {renderRadarTimeline()}
                  <div className="layer-toggles">{layerChips}</div>
                </div>

                {/* Lightning panel */}
                <div
                  className={`radar-tab-panel${
                    activeRadarTab === 'lightning' ? ' active' : ''
                  }`}
                >
                  <div
                    className={`status-banner ${li.cls}`}
                    style={
                      !li.cls
                        ? {
                            background: 'var(--surface-1)',
                            border: '1px solid var(--border)',
                          }
                        : undefined
                    }
                  >
                    <div
                      className="status-dot"
                      style={
                        !li.cls
                          ? { background: 'var(--text-muted)' }
                          : undefined
                      }
                    />
                    <div className="status-text">
                      {li.text} <div className="status-sub">{li.sub}</div>
                    </div>
                  </div>
                  <div className="lightning-stats-grid">
                    <StatTile
                      value={
                        lightning && lightning.nearest != null
                          ? lightning.nearest
                          : '—'
                      }
                      unit="mi"
                      label="Nearest Strike"
                    />
                    <StatTile
                      value={
                        lightning && lightning.sinceLast != null
                          ? lightning.sinceLast
                          : '—'
                      }
                      unit="min"
                      label="Since Last"
                    />
                    <StatTile
                      value={
                        lightning && lightning.count != null
                          ? lightning.count
                          : '—'
                      }
                      label="Strikes (30mi/30m)"
                    />
                    <StatTile
                      value={
                        lightning && lightning.bearing ? lightning.bearing : '—'
                      }
                      label="Bearing"
                      valueColor="var(--lightning-caution)"
                    />
                  </div>
                </div>

                {/* Storm Cells panel */}
                <div
                  className={`radar-tab-panel${
                    activeRadarTab === 'storms' ? ' active' : ''
                  }`}
                >
                  {vm.stormCells.length ? (
                    <div className="storm-cell-list">
                      {vm.stormCells.map((s, i) => (
                        <div className="storm-cell-item" key={i}>
                          <div className={`storm-severity-dot ${s.severity}`} />
                          <div className="storm-cell-info">
                            <div
                              className="storm-cell-type"
                              style={
                                s.severity === 'rotating'
                                  ? { color: 'var(--alert-severe)' }
                                  : undefined
                              }
                            >
                              {s.type}
                            </div>
                            <div className="storm-cell-detail">
                              {s.detail || '—'}
                            </div>
                          </div>
                          <div className="storm-cell-eta">
                            <span className="eta-label">ETA</span>
                            {s.eta}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: 16,
                        color: 'var(--text-muted)',
                        fontSize: 13,
                        textAlign: 'center',
                      }}
                    >
                      No active storm cells near this venue.
                    </div>
                  )}
                </div>

                {/* Alerts panel */}
                <div
                  className={`radar-tab-panel${
                    activeRadarTab === 'alerts' ? ' active' : ''
                  }`}
                >
                  <div
                    style={{
                      padding: 16,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                    }}
                  >
                    {vm.alerts.length ? (
                      vm.alerts.map((a, i) => (
                        <div
                          key={i}
                          style={{
                            background: 'rgba(250,70,22,0.08)',
                            border: '1px solid rgba(250,70,22,0.3)',
                            borderRadius: 10,
                            padding: '16px 18px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              marginBottom: 12,
                            }}
                          >
                            <Icon
                              name="triangle-alert"
                              size={22}
                              color="var(--orange)"
                              strokeWidth={2}
                            />
                            <div>
                              <div
                                style={{
                                  fontWeight: 700,
                                  fontSize: 15,
                                  color: 'var(--text-primary)',
                                }}
                              >
                                {a.type}
                              </div>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: 'var(--text-muted)',
                                  marginTop: 2,
                                }}
                              >
                                {[
                                  a.county,
                                  a.expires ? `Until ${a.expires}` : null,
                                ]
                                  .filter(Boolean)
                                  .join(' — ')}
                              </div>
                            </div>
                          </div>
                          {a.body && (
                            <div
                              style={{
                                fontSize: 13,
                                lineHeight: 1.55,
                                color: 'var(--text-secondary)',
                              }}
                            >
                              {a.body}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          fontSize: 13,
                          color: 'var(--text-muted)',
                          textAlign: 'center',
                        }}
                      >
                        No active alerts for this venue.
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>

            {/* ROW 3: PRECIP TILES */}
            <div className="precip-row">
              {/* Precip Summary */}
              <Card className="precip-compact-card">
                <CardHeader
                  icon="droplet"
                  label="Precipitation"
                  tip="Rainfall totals for the last 24 hours and forecast for the next 24 hours, plus current rain rate and next expected rain time."
                  actions={<ExpandButton onClick={() => openModal('precip')} />}
                />
                <div className="next-rain-compact">
                  <Icon name="droplet" size={16} color="rgba(79,195,247,0.7)" />
                  <div>
                    <div className="nr-label">Next Rain</div>
                    <div className="nr-time">
                      {vm.precip.nextRainAt || 'None expected'}
                    </div>
                  </div>
                </div>
                <div className="precip-totals">
                  <div className="precip-total-box">
                    <div className="precip-total-label">Last 24h</div>
                    <div className="precip-total-value">
                      {vm.precip.last24 || '—'}
                      <span className="unit"> in</span>
                    </div>
                  </div>
                  <div className="precip-total-box">
                    <div className="precip-total-label">Next 24h</div>
                    <div className="precip-total-value">
                      {vm.precip.next24 || '—'}
                      <span className="unit"> in</span>
                    </div>
                  </div>
                </div>
                <div className="precip-rate-compact">
                  <div className="precip-rate-dot" />
                  <span className="rate-label">Current Rate</span>
                  <span className="rate-value">{vm.precip.rate || '—'}</span>
                </div>
              </Card>

              {/* Hourly Precip Chart */}
              <Card className="tile-card">
                <CardHeader
                  icon="clock"
                  label="Hourly Precip"
                  tip="Hour-by-hour precipitation forecast showing expected rain intensity. Expand for a full hourly breakdown and expected rain windows."
                  actions={
                    <ExpandButton onClick={() => openModal('precipTimeline')} />
                  }
                />
                <div className="precip-chart-compact">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 6,
                      flex: 1,
                      minHeight: 0,
                    }}
                  >
                    {vm.hourlyBars.map((d, i) => {
                      const height =
                        d.amount > 0 ? Math.max(10, (d.amount / 0.5) * 100) : 4;
                      const hasRain = d.amount > 0;
                      return (
                        <div
                          key={i}
                          style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            height: '100%',
                            justifyContent: 'flex-end',
                          }}
                        >
                          {hasRain && (
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: 'var(--rain-moderate)',
                                marginBottom: 3,
                              }}
                            >
                              {d.amount.toFixed(2)}"
                            </div>
                          )}
                          <div
                            style={{
                              width: '100%',
                              height: `${height}%`,
                              background: getBarColor(d.amount),
                              borderRadius: '3px 3px 0 0',
                              minHeight: 3,
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="precip-time-row">
                    {vm.hourlyBars.map((d, i) => {
                      const [num, ampm] = (d.time || '').split(' ');
                      return (
                        <div className="precip-time-slot" key={i}>
                          {num}
                          {ampm ? (
                            <span className="pt-ampm"> {ampm}</span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="timeline-legend-compact">
                  {[
                    ['Light', 'var(--rain-light)'],
                    ['Moderate', 'var(--rain-moderate)'],
                    ['Heavy', 'var(--rain-heavy)'],
                    ['Extreme', 'var(--rain-extreme)'],
                  ].map(([label, color]) => (
                    <div className="legend-item-sm" key={label}>
                      <div
                        className="legend-swatch-sm"
                        style={{ background: color }}
                      />{' '}
                      {label}
                    </div>
                  ))}
                </div>
              </Card>

              {/* Lightning Tracker */}
              <Card className="tile-card">
                <CardHeader
                  icon="zap"
                  label="Lightning"
                  tip="Tracks nearby lightning strikes in real time. Shows nearest strike distance, strike rate, and approach direction. Green = clear, Orange = caution, Red = danger."
                  actions={
                    <ExpandButton onClick={() => openModal('lightning')} />
                  }
                />
                <div
                  className={`lightning-compact-status ${li.cls}`}
                  style={
                    !li.cls
                      ? {
                          background: 'var(--surface-1)',
                          border: '1px solid var(--border)',
                        }
                      : undefined
                  }
                >
                  <div
                    className="lc-dot"
                    style={
                      !li.cls
                        ? { background: 'var(--text-muted)', animation: 'none' }
                        : undefined
                    }
                  />
                  <div>
                    <div className="lc-text">{li.text}</div>
                    <div className="lc-sub">{li.sub}</div>
                  </div>
                </div>
                <div className="lightning-mini-stats">
                  <StatTile
                    size="sm"
                    value={
                      lightning && lightning.nearest != null
                        ? lightning.nearest
                        : '—'
                    }
                    unit="mi"
                    label="Nearest"
                  />
                  <StatTile
                    size="sm"
                    value={
                      lightning && lightning.sinceLast != null
                        ? lightning.sinceLast
                        : '—'
                    }
                    unit="min"
                    label="Last Strike"
                  />
                  <StatTile
                    size="sm"
                    value={
                      lightning && lightning.count != null
                        ? lightning.count
                        : '—'
                    }
                    label="Strikes/30m"
                  />
                  <StatTile
                    size="sm"
                    value={
                      lightning && lightning.bearing ? lightning.bearing : '—'
                    }
                    label="Bearing"
                    valueColor="var(--lightning-caution)"
                  />
                </div>
                <div
                  className="radar-hint"
                  {...buttonize(() => viewOnRadar('lightning'))}
                >
                  <span className="rh-icon">
                    <Icon
                      name="zap"
                      size={15}
                      color="var(--orange)"
                      strokeWidth={2}
                    />
                  </span>
                  <span className="radar-hint-text">
                    Live strikes plot on the radar map, fading as they age.
                  </span>
                  <span className="radar-hint-cta">View radar &rarr;</span>
                </div>
              </Card>

              {/* Storm Cells */}
              <Card className="tile-card">
                <CardHeader
                  icon="cloud-lightning"
                  label="Storm Cells"
                  tip="Radar-identified storm cells near your venue. Shows cell type (hail, thunderstorm, rotating), movement direction and speed, and estimated arrival time."
                  actions={<ExpandButton onClick={() => openModal('storms')} />}
                />
                {vm.stormCells.length ? (
                  <div className="storm-compact-list">
                    {vm.stormCells.map((s, i) => (
                      <div className="storm-compact-item" key={i}>
                        <div className={`sc-dot ${s.severity}`} />
                        <div className="sc-info">
                          <div
                            className="sc-type"
                            style={
                              s.severity === 'rotating'
                                ? { color: 'var(--alert-severe)' }
                                : undefined
                            }
                          >
                            {s.type}
                          </div>
                          <div className="sc-detail">{s.detail || '—'}</div>
                        </div>
                        <div className="sc-eta">{s.eta}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      padding: '18px 4px',
                      color: 'var(--text-muted)',
                      fontSize: 12,
                      textAlign: 'center',
                    }}
                  >
                    No active storm cells.
                  </div>
                )}
                <div className="card-key">
                  <div className="card-key-title">Cell Types</div>
                  <div className="key-row">
                    <span className="key-pill">
                      <span className="k-dot k-hail" />
                      Hail
                    </span>
                    <span className="key-pill">
                      <span className="k-dot k-storm" />
                      Thunderstorm
                    </span>
                    <span className="key-pill">
                      <span className="k-dot k-rotating" />
                      Rotating
                    </span>
                  </div>
                </div>
                <div
                  className="radar-hint"
                  {...buttonize(() => viewOnRadar('storms'))}
                >
                  <span className="rh-icon">
                    <Icon
                      name="cloud-lightning"
                      size={15}
                      color="var(--orange)"
                      strokeWidth={2}
                    />
                  </span>
                  <span className="radar-hint-text">
                    Track each cell's path and arrival live on radar.
                  </span>
                  <span className="radar-hint-cta">View radar &rarr;</span>
                </div>
              </Card>
            </div>

            {/* ROW 4: FORECAST */}
            <Card className="forecast-card">
              <CardHeader
                icon="calendar"
                label="Extended Forecast"
                tip="Multi-day forecast with high/low temps, precipitation chance, and conditions. Toggle between 7-day and 10-day views. Tap a day for hourly detail."
                actions={
                  <>
                    <div className="forecast-range-chips">
                      <PillToggle
                        variant="range"
                        active={forecastRange === 7}
                        onClick={() => setForecastRange(7)}
                      >
                        7 Day
                      </PillToggle>
                      <PillToggle
                        variant="range"
                        active={forecastRange === 10}
                        onClick={() => setForecastRange(10)}
                      >
                        10 Day
                      </PillToggle>
                    </div>
                    <span style={{ marginLeft: 8, display: 'inline-flex' }}>
                      <ExpandButton onClick={() => openModal('forecast')} />
                    </span>
                  </>
                }
              />
              {visibleDays.length ? (
                <div
                  className="forecast-days"
                  style={{
                    gridTemplateColumns: `repeat(${visibleDays.length}, 1fr)`,
                  }}
                >
                  {visibleDays.map((d, i) => (
                    <div
                      className={`day-card${
                        i === selectedDay ? ' selected' : ''
                      }`}
                      key={`${d.day}-${d.date}-${i}`}
                      {...buttonize(() => selectDay(i, d))}
                    >
                      <div className="day-name">
                        {d.isToday ? 'Today' : d.day}
                      </div>
                      <div className="day-date">{d.date}</div>
                      <div className="day-icon">
                        {d.icon ? (
                          <img src={getIconUrl(d.icon)} alt="" />
                        ) : null}
                      </div>
                      <div className="day-temps">
                        {d.high} <span className="low">/ {d.low}</span>
                      </div>
                      <div className="day-precip">
                        <Icon
                          name="droplet"
                          size={13}
                          color="var(--rain-light)"
                          strokeWidth={2}
                        />
                        {d.precipPct == null ? '—' : `${d.precipPct}%`}
                      </div>
                      <div className="day-rain-total">{d.rain} in</div>
                      {d.wind && <div className="day-wind">{d.wind} mph</div>}
                      {d.summary && (
                        <div className="day-summary-icon">
                          <Icon
                            name="info"
                            size={12}
                            color="var(--orange)"
                            strokeWidth={2}
                          />
                          <div className="day-summary-popup">{d.summary}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: '24px 4px',
                    color: 'var(--text-muted)',
                    fontSize: 13,
                    textAlign: 'center',
                  }}
                >
                  Forecast unavailable.
                </div>
              )}
            </Card>
          </div>
        )}

        {/* DETAIL MODAL — all detail views driven by the live view model */}
        <Modal
          open={!!modal}
          onClose={closeModal}
          fullscreen={!!isRadarModal}
          icon={modalIconName}
          title={modalTitle}
        >
          {modal &&
            (isRadarModal ? (
              <>
                {renderRadarMap(
                  1.6,
                  { height: 'calc(94vh - 230px)', minHeight: 400 },
                  modalMapRef
                )}
                {renderRadarTimeline()}
                <div className="layer-toggles" style={{ marginBottom: 16 }}>
                  {layerChips}
                </div>
              </>
            ) : modal.type === 'hourly' ? (
              renderHourlyModalBody(modal.day)
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: buildModalBody(modal.type, vm, venueName),
                }}
              />
            ))}
        </Modal>

        {/* PHRASES MODAL */}
        <div
          className={`phrases-modal-overlay${phrasesOpen ? ' active' : ''}`}
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPhrasesOpen(false);
          }}
        >
          <div className="phrases-modal" role="dialog" aria-modal="true">
            <div className="phrases-modal-header">
              <div className="phrases-modal-title">
                <Icon name="info" size={18} color="var(--orange)" />
                Forecast Summary
              </div>
              <button
                type="button"
                className="phrases-modal-close"
                onClick={() => setPhrasesOpen(false)}
                aria-label="Close"
              >
                <Icon name="x" size={14} strokeWidth={2.5} />
              </button>
            </div>
            <div className="phrases-modal-body">
              {vm && vm.forecast[0] && vm.forecast[0].summary
                ? vm.forecast[0].summary
                : 'Forecast summary is not available right now.'}
            </div>
            <div className="phrases-modal-source">Powered by Xweather</div>
          </div>
        </div>
      </WCRoot>
    </ThemeProvider>
  );
};

export default WeatherCenter;
